import { type CSSProperties, useState } from "react";
import {
    getEffectPatternBackgroundStyle,
    getEffectPatternDotColor,
    getSystemFillColor,
    NAV_CARD_BACKGROUND_TRANSITION,
    NAV_HOVER_LEVEL_DIFF,
} from "../colors";
import { getContentForLevel } from "../content";
import { type AreaNavItem, getLevelConfig } from "../modules/emissions/data-config";
import type { ChartDataWithEffect } from "../modules/emissions/EmissionsChart";
import {
    formatNumberToTwoDecimals,
    getRadioOptionsLevel4,
    RADIO_VIEW_VALUES,
    type RadioViewValue,
    sortNavItemsByEmissionsDesc,
} from "../modules/emissions/emissions-chart-utils";
import { getEnovaActivityDataForSelection } from "../modules/enova-activity-utils";
import { getMarketDataForSelection, type MarketDataPoint } from "../modules/market/market-chart-utils";
import type { Selection } from "./utils";

export function useChartState() {
    const [level, setLevel] = useState(1);
    const [selection, setSelection] = useState<Selection>({
        level1: null,
        level2: null,
        level3: null,
        level4: null,
    });
    const [selectedView, setSelectedView] = useState<RadioViewValue>(RADIO_VIEW_VALUES.EMISSIONS);
    const [hoveredNavKey, setHoveredNavKey] = useState<string | null>(null);
    const [isEffectCardHovered, setIsEffectCardHovered] = useState(false);

    const content = getContentForLevel(level, selection);
    const currentLevelConfig = getLevelConfig(level, selection);
    const level1ConfigData = getLevelConfig(1, selection);
    const level2ConfigData = getLevelConfig(2, selection);
    const level3ConfigData = getLevelConfig(3, selection);
    const navItems = currentLevelConfig.navItems;
    const chartData = currentLevelConfig.data;
    const navItemsForDisplay =
        level === 2 || level === 3 ? sortNavItemsByEmissionsDesc(2024, navItems, chartData) : navItems;
    const shouldUseRankBasedColors = (level === 2 || level === 3) && Boolean(selection.level1);
    const navItemsForChart = shouldUseRankBasedColors
        ? navItemsForDisplay.map((item, rankIndex) => ({
              ...item,
              fill: getSystemFillColor(selection.level1 as string, rankIndex),
              hoverFill: getSystemFillColor(selection.level1 as string, rankIndex - NAV_HOVER_LEVEL_DIFF),
          }))
        : navItems;
    const enovaSystemKey = selection.level1;

    const chartDataWithEffect = chartData.map((d): ChartDataWithEffect => {
        const totalAreas = navItems.reduce((sum, nav) => sum + (d[nav.area] || 0), 0);
        const utenEnovaValue = typeof d.utenEnova === "number" ? d.utenEnova : null;
        const effectDifference =
            utenEnovaValue !== null && utenEnovaValue > totalAreas ? utenEnovaValue - totalAreas : 0;
        return {
            ...d,
            utenEnova: utenEnovaValue,
            totalAreas: typeof totalAreas === "number" ? totalAreas : null,
            effectDifference,
        };
    });

    const isLevelFour = level === 4;
    const marketData: MarketDataPoint[] | null = isLevelFour ? getMarketDataForSelection(selection) : null;
    const enovaActivityData = isLevelFour ? getEnovaActivityDataForSelection(selection) : null;
    const hasMarketData = Boolean(marketData && marketData.length > 0);
    const canShowEnovaActivityViews = Boolean(enovaActivityData && enovaActivityData.length > 0);

    const resolvedSelectedView = (() => {
        if (selectedView === RADIO_VIEW_VALUES.MARKET && (!isLevelFour || !hasMarketData)) {
            return RADIO_VIEW_VALUES.EMISSIONS;
        }
        if (selectedView === RADIO_VIEW_VALUES.ENOVA_ACCUMULATED && (!isLevelFour || !canShowEnovaActivityViews)) {
            return RADIO_VIEW_VALUES.EMISSIONS;
        }
        if (selectedView === RADIO_VIEW_VALUES.ENOVA_YEARLY && (!isLevelFour || !canShowEnovaActivityViews)) {
            return RADIO_VIEW_VALUES.EMISSIONS;
        }
        return selectedView;
    })();

    const selectedRadioLabel = getRadioOptionsLevel4().find((opt) => opt.value === resolvedSelectedView)?.label;
    const isMarketView = isLevelFour && resolvedSelectedView === RADIO_VIEW_VALUES.MARKET && hasMarketData;
    const isAccumulatedView = isLevelFour && resolvedSelectedView === RADIO_VIEW_VALUES.ENOVA_ACCUMULATED;
    const isYearlyView = isLevelFour && resolvedSelectedView === RADIO_VIEW_VALUES.ENOVA_YEARLY;

    const accumulatedDifferenceValue = chartData
        .filter((d) => d.year >= 2022 && d.year <= 2050)
        .reduce((sum, d) => {
            const totalAreas = d.area1 + d.area2 + d.area3 + d.area4 + d.area5;
            return sum + (d.utenEnova - totalAreas);
        }, 0);
    const roundedAccumulatedDifferenceValue = Math.round(accumulatedDifferenceValue * 100) / 100;
    const normalizedAccumulatedDifferenceValue = Object.is(roundedAccumulatedDifferenceValue, -0)
        ? 0
        : roundedAccumulatedDifferenceValue;
    const canEnterLevel4 = normalizedAccumulatedDifferenceValue > 0;
    const accumulatedDifference = formatNumberToTwoDecimals(normalizedAccumulatedDifferenceValue);

    const effectPatternDotColor = getEffectPatternDotColor(isEffectCardHovered);
    const effectPatternBackground = getEffectPatternBackgroundStyle(effectPatternDotColor);

    const getBackLabel = (isNav: boolean | undefined, suffixForLevel4?: string) => {
        let item: AreaNavItem | undefined;
        if (level === 2 && selection.level1) {
            item = level1ConfigData.navItems.find((nav) => nav.key === selection.level1);
            return item?.label || "Tilbake";
        } else if (level === 3 && selection.level2) {
            item = level2ConfigData.navItems.find((nav) => nav.key === selection.level2);
            return item?.label || "Tilbake";
        } else if (level === 4 && selection.level3) {
            if (isNav) {
                item = level2ConfigData.navItems.find((nav) => nav.key === selection.level2);
            } else {
                item = level3ConfigData.navItems.find((nav) => nav.key === selection.level3);
            }
            return `${item?.label}${suffixForLevel4 ? `: ${suffixForLevel4}` : ""}` || "Tilbake";
        }
        return "";
    };

    const legendHeading =
        getBackLabel(undefined, level === 4 ? selectedRadioLabel : undefined) ||
        "Forventede utslipp fra gods- og persontransport";

    const getNavCardStyle = (navItem: AreaNavItem, displayIndex?: number): CSSProperties => {
        const isHovered = hoveredNavKey === navItem.key;
        const rankIndex = typeof displayIndex === "number" ? displayIndex : 0;
        const baseBackground = shouldUseRankBasedColors
            ? getSystemFillColor(selection.level1 as string, rankIndex)
            : navItem.fill;
        const hoverBackground = shouldUseRankBasedColors
            ? getSystemFillColor(selection.level1 as string, rankIndex - NAV_HOVER_LEVEL_DIFF)
            : (navItem.hoverFill ?? navItem.fill);
        return {
            background: isHovered ? hoverBackground : baseBackground,
            // The "-nav-oN" fill tokens (colors.css) stay equally dark in both themes, so a
            // literal white (not a theme-flipping token like --ds-color-surface-default, which
            // resolves dark in dark mode) is what clears 4.5:1 (WCAG 1.4.3) against them here.
            color: "#fff",
            transition: NAV_CARD_BACKGROUND_TRANSITION,
        };
    };

    const handleNavClick = (itemKey: string) => {
        if (level === 1) {
            setSelection((prev) => ({ ...prev, level1: itemKey, level2: null, level3: null, level4: null }));
            setLevel(2);
        } else if (level === 2) {
            setSelection((prev) => ({ ...prev, level2: itemKey, level3: null, level4: null }));
            setLevel(3);
        } else if (level === 3) {
            if (!canEnterLevel4) return;
            setSelection((prev) => ({ ...prev, level3: itemKey, level4: null }));
            setLevel(4);
            setSelectedView(RADIO_VIEW_VALUES.EMISSIONS);
        }
        setHoveredNavKey(null);
    };

    const handleBack = () => {
        setLevel((prev) => (prev > 1 ? prev - 1 : 1));
        setHoveredNavKey(null);
    };

    const handleBreadcrumbNavigate = (targetLevel: number) => {
        if (targetLevel >= level) return;
        setLevel(targetLevel);
        setHoveredNavKey(null);
        setSelection((prev) => {
            if (targetLevel === 1) return { level1: null, level2: null, level3: null, level4: null };
            if (targetLevel === 2) return { ...prev, level3: null, level4: null };
            if (targetLevel === 3) return { ...prev, level4: null };
            return prev;
        });
    };

    return {
        level,
        selection,
        content,
        currentLevelConfig,
        level1ConfigData,
        level2ConfigData,
        level3ConfigData,
        navItems,
        chartData,
        chartDataWithEffect,
        navItemsForDisplay,
        navItemsForChart,
        enovaSystemKey,
        marketData,
        enovaActivityData,
        hasMarketData,
        canShowEnovaActivityViews,
        resolvedSelectedView,
        isMarketView,
        isAccumulatedView,
        isYearlyView,
        legendHeading,
        normalizedAccumulatedDifferenceValue,
        accumulatedDifference,
        canEnterLevel4,
        effectPatternDotColor,
        effectPatternBackground,
        isEffectCardHovered,
        hoveredNavKey,
        setIsEffectCardHovered,
        setHoveredNavKey,
        setSelectedView,
        getNavCardStyle,
        getBackLabel,
        handleNavClick,
        handleBack,
        handleBreadcrumbNavigate,
    };
}
